<template>
    <div class="datafield-validation-field">
        <button-select
            v-if="isDatafieldOptions"
            v-model="option"
            :options="options"
            :class="{ error }" />

        <ui5-input
            v-else
            ref="input"
            show-suggestions
            placeholder="Kies een veld of waarde"
            :value="field"
            @input="field = $event.target.value"
            @change="field = $event.target.value"
            :class="{ error }" />

        <p
            v-if="isOptionOutOfUse"
            class="datafield-validation-field__error"
            v-html="optionOutOfUseMessage"></p>
    </div>
</template>

<script>
import ButtonSelect from './ButtonSelect.vue';

export default {
    props: {
        name: {
            type: String,
            required: true,
        },
        statement: {
            type: Object,
            required: true,
        },
        datafields: {
            type: Array,
            required: true,
        },
        error: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            search: '',
        };
    },
    computed: {
        datafield() {
            return this.datafields.find(
                (df) => df.datafieldId == this.statement.datafieldId
            );
        },
        isDatafieldOptions() {
            return (
                this.datafield !== undefined &&
                this.datafield.datatypeText === 'options'
            );
        },
        options() {
            let options = [];
            let fieldOptions = [];

            // Get datafield options (Opties)
            if (this.datafield.fielddata) {
                options = this.datafield.fielddata
                    .filter((fd) => fd.inUse === true)
                    .map((datafieldOption) => ({
                        label:
                            datafieldOption.translations.dataoption['nl-BE'] +
                            ' (optie)',
                        value: 'dfo_' + datafieldOption.datafieldoptionId,
                        isOption: true,
                    }))
                    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
            }

            // Get datafields (Velden)
            fieldOptions = this.datafields
                .map((datafield) => ({
                    label: datafield.label + ' (veld)',
                    value: 'df_' + datafield.datafieldId,
                    isOption: false,
                }))
                .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

            // Create grouped structure with headers
            let groupedOptions = [];

            if (options.length > 0) {
                groupedOptions.push({
                    label: '--- Opties ---',
                    value: null,
                    disabled: true,
                    isHeader: true,
                });
                groupedOptions = groupedOptions.concat(options);
            }

            if (fieldOptions.length > 0) {
                if (groupedOptions.length > 0) {
                    groupedOptions.push({
                        label: '--- Velden ---',
                        value: null,
                        disabled: true,
                        isHeader: true,
                    });
                } else {
                    groupedOptions.push({
                        label: '--- Velden ---',
                        value: null,
                        disabled: true,
                        isHeader: true,
                    });
                }
                groupedOptions = groupedOptions.concat(fieldOptions);
            }

            return groupedOptions;
        },
        datafieldName() {
            return this.name + 'DatafieldId';
        },
        datafieldOptionName() {
            return this.name + 'DatafieldOptionId';
        },
        valueName() {
            return this.name + 'Value';
        },
        field: {
            get() {
                // Datafield
                if (this.statement[this.datafieldName] !== null) {
                    const datafield = this.datafields.find(
                        (df) =>
                            df.datafieldId == this.statement[this.datafieldName]
                    );
                    if (datafield) {
                        return datafield.label;
                    }
                }

                // Value
                if (this.statement[this.valueName] !== null) {
                    return this.statement[this.valueName].toString();
                }

                // Search
                return this.search;
            },
            set(value) {
                // Datafield
                const datafield = this.datafields.find(
                    (df) =>
                        df.label &&
                        df.label.toUpperCase() === value.toUpperCase()
                );

                if (datafield) {
                    this.statement[this.datafieldName] = parseInt(
                        datafield.datafieldId
                    );
                    this.statement[this.datafieldOptionName] = null;
                    this.statement[this.valueName] = null;
                    this.search = datafield.label;
                    return;
                }

                // Value
                if (
                    (!isNaN(value) || !isNaN(value.replace(',', '.'))) &&
                    value.length > 0
                ) {
                    this.statement[this.datafieldName] = null;
                    this.statement[this.datafieldOptionName] = null;
                    this.statement[this.valueName] = !isNaN(value)
                        ? value
                        : parseFloat(value.replace(',', '.'));
                    this.search = value;
                    return;
                }

                // Search
                this.statement[this.datafieldName] = null;
                this.statement[this.datafieldOptionName] = null;
                this.statement[this.valueName] = null;
                this.search = value;
            },
        },
        option: {
            set(value) {
                // Datafield
                if (value && value.startsWith('df_')) {
                    value = value.slice(3);
                    const datafield = this.datafields.find(
                        (df) => df.datafieldId == value
                    );
                    if (datafield) {
                        this.statement[this.datafieldName] = parseInt(value);
                    }

                    this.statement[this.datafieldOptionName] = null;
                    this.statement[this.valueName] = null;
                    this.search = null;
                    return;
                }

                // Datafield option
                if (value && value.startsWith('dfo_')) {
                    value = value.slice(4);
                    const datafieldOption = this.datafield.fielddata.find(
                        (dfo) => dfo.datafieldoptionId == value
                    );
                    if (datafieldOption) {
                        this.statement[this.datafieldOptionName] =
                            parseInt(value);
                    }

                    this.statement[this.datafieldName] = null;
                    this.statement[this.valueName] = null;
                    this.search = null;
                    return;
                }

                this.statement[this.datafieldName] = null;
                this.statement[this.datafieldOptionName] = null;
                this.statement[this.valueName] = null;
                this.search = null;
            },
            get() {
                if (this.statement[this.datafieldName] !== null) {
                    return 'df_' + this.statement[this.datafieldName];
                }

                if (this.statement[this.datafieldOptionName] !== null) {
                    return 'dfo_' + this.statement[this.datafieldOptionName];
                }

                return null;
            },
        },
        isOptionOutOfUse() {
            if (this.option && this.option.startsWith('dfo_')) {
                const optionId = this.option.slice(4);
                const datafieldOption = this.datafield?.fielddata?.find(
                    (dfo) => dfo.datafieldoptionId == optionId
                );
                return !datafieldOption || !datafieldOption.inUse;
            }

            if (this.option && this.option.startsWith('df_')) {
                const datafieldId = this.option.slice(3);
                const datafield = this.datafields?.find(
                    (df) => df.datafieldId == datafieldId
                );
                return !datafield || !datafield.inUse;
            }

            return false;
        },
        optionOutOfUseMessage() {
            if (!this.option) {
                return null;
            }

            if (this.option && this.option.startsWith('dfo_')) {
                const optionId = this.option.slice(4);
                const datafieldOption = this.datafield.fielddata.find(
                    (dfo) => dfo.datafieldoptionId == optionId
                );
                if (datafieldOption) {
                    const optionLabel =
                        datafieldOption.translations.dataoption['nl-BE'];
                    return `Dataveld optie <b>${optionLabel}</b> is niet meer beschikbaar. Kies een andere optie.`;
                }

                return 'De geselecteerde dataveld optie is niet meer beschikbaar';
            } else if (this.option && this.option.startsWith('df_')) {
                const datafieldId = this.option.slice(3);
                const datafield = this.datafields.find(
                    (df) => df.datafieldId == datafieldId
                );
                if (datafield) {
                    const optionLabel = datafield.label;
                    return `Dataveld <b>${optionLabel}</b> is niet meer beschikbaar.Kies een ander veld.`;
                }

                return 'Het geselecteerde dataveld is niet meer beschikbaar';
            }
        },
    },
    watch: {
        search(value) {
            if (!this.$refs.input) {
                return;
            }

            let suggestionDatafields = this.datafields;

            // Filter
            if (value) {
                suggestionDatafields = suggestionDatafields.filter((df) => {
                    return (
                        df.label &&
                        df.label.toUpperCase().indexOf(value.toUpperCase()) ===
                            0
                    );
                });
            }

            // remove the previous suggestions
            [].slice.call(this.$refs.input.children).forEach((child) => {
                this.$refs.input.removeChild(child);
            });

            // add the new suggestions according to the  user input
            suggestionDatafields.forEach((df) => {
                const li = document.createElement('ui5-suggestion-item');
                li.text = df.label;
                this.$refs.input.appendChild(li);
            });
        },
    },
    components: {
        ButtonSelect,
    },
};
</script>
